import { isPlatformBrowser, isPlatformServer, NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Data, Router, RouterLink, RouterOutlet } from '@angular/router';
import { WithDynamicContent } from '@woolworthsnz/dynamic-content';
import {
	getHouseholdPreference,
	mapHouseholdToServings,
	MealPlanningData,
	MealPlanningService,
	mealtimeSlug,
	placeholderRecipeStamp,
	RecipeService,
	Stamp,
	DetailsHeaderComponent,
	GridContainerComponent,
	MethodComponent,
	SeoComponent,
} from '@woolworthsnz/recipe';
import {
	ERROR_PATH,
	MEAL_PLANNING_CHOOSE_MEALS_PATH,
	MEAL_PLANNING_PATH,
	MODAL_ROUTES,
	NOT_FOUND_PATH,
	RECIPES_PATH,
} from '@woolworthsnz/shop';
import {
	ButtonState,
	DatalayerService,
	FeatureService,
	isStatusCode,
	LocalStorageService,
	ModalEvent,
	ModalOverlayService,
	ModalRoutingService,
	NotificationType,
	parseStringToIntOrDefault,
	ShopperService,
	slugify,
	ButtonComponent,
	WappleLoadingComponent,
	RESPONSE,
} from '@woolworthsnz/styleguide';
import { ContextResponse, RecipeResponse } from '@woolworthsnz/trader-api';
import { TealiumUtagService, safeStringSplit } from '@woolworthsnz/analytics';
import { Response } from 'express';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { mapRecipeResponseToRecipeStampLocal, mapToStamps } from 'libs/recipe/src/lib/helpers/stamp.helper';

@WithDynamicContent()
@Component({
	selector: 'wnz-recipe-details',
	templateUrl: 'recipe-details.page.html',
	styleUrls: ['./recipe-details.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		WappleLoadingComponent,
		SeoComponent,
		DetailsHeaderComponent,
		MethodComponent,
		GridContainerComponent,
		ButtonComponent,
		RouterLink,
		RouterOutlet,
		AsyncPipe,
	],
})
export class RecipeDetailsPage implements OnInit, OnDestroy {
	recipe$: Observable<RecipeResponse>;
	recipeId$: Observable<number>;
	relatedRecipes$: Observable<Stamp[]>;
	errors$ = new BehaviorSubject(false);
	isLoading$ = new BehaviorSubject(false);
	recipeSaved$ = new BehaviorSubject(false);
	saveButtonState$ = new BehaviorSubject<ButtonState>(ButtonState.default);
	recipeId?: number;
	enableAddToTrolley$: Observable<boolean>;
	landingPageRoute = `/${RECIPES_PATH}`;
	modalOutletName = MODAL_ROUTES.RECIPE_MODAL;
	isMealPlanningContext = false;
	mealPlanningData$: Observable<MealPlanningData>;
	isRecipeInMealPlan$: Observable<boolean>;
	isAddedToMealPlan$: Observable<boolean>;
	isMealPlanningEnabled$: Observable<boolean>;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		private route: ActivatedRoute,
		private recipeService: RecipeService,
		private shopperService: ShopperService,
		private modalOverlayService: ModalOverlayService,
		private modalRoutingService: ModalRoutingService,
		private datalayerService: DatalayerService,
		private featureService: FeatureService,
		private router: Router,
		private tealiumService: TealiumUtagService,
		@Optional() @Inject(RESPONSE) private response: Response,
		@Inject(PLATFORM_ID) private platformId: Object,
		private localStorageService: LocalStorageService,
		private mealPlanningService: MealPlanningService
	) {}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	ngOnInit(): void {
		this.recipeId$ = this.route.paramMap.pipe(takeUntil(this.destroyed$)).pipe(
			map((paramMap) => parseStringToIntOrDefault(paramMap.get('recipeId'), 0)),
			filter(Boolean),
			tap((recipeId) => (this.recipeId = recipeId)),
			shareReplay(1)
		);

		this.recipe$ = this.recipeId$.pipe(switchMap(this.fetchRecipe.bind(this)), shareReplay(1));

		// This call isn't combined with the one above as the recipe can load without us knowing if it is saved or not.
		// This should return true for 204s and false for anything else including 404s (Logged in, not saved) or 403s (Logged out)
		this.recipeId$
			.pipe(
				takeUntil(this.destroyed$),
				switchMap((recipeId) => this.recipeService.isRecipeSaved(recipeId)),
				isStatusCode(204)
			)
			.subscribe((res) => {
				this.recipeSaved$.next(res);
			});

		if (isPlatformServer(this.platformId)) {
			// just use placeholders for SSR since the call seems expensive
			this.relatedRecipes$ = of(Array(3).fill({ recipe: placeholderRecipeStamp }));
		} else {
			this.relatedRecipes$ = this.recipeId$.pipe(
				takeUntil(this.destroyed$),
				switchMap((recipeId) => this.recipeService.getRelatedRecipes(recipeId, 3)),
				map(mapToStamps),
				catchError((err) => {
					this.errors$.next(true);
					return throwError(() => err);
				})
			);
		}

		this.enableAddToTrolley$ = this.featureService.state$.pipe(
			map(() => this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RecipesAddToTrolley))
		);

		this.initialiseMealPlanning();
	}

	initialiseMealPlanning(): void {
		this.isMealPlanningEnabled$ = this.featureService
			.isEnabled(ContextResponse.EnabledFeaturesEnum.MealPlanning)
			.pipe(filter(Boolean));

		this.mealPlanningData$ = this.isMealPlanningEnabled$.pipe(
			switchMap(() =>
				this.route.data.pipe(
					take(1),
					map((data: Data) => data.mealPlanning)
				)
			)
		);

		this.isMealPlanningContext = this.localStorageService.getItem('isMealPlanningContext');
		this.localStorageService.removeItem('isMealPlanningContext');

		this.isAddedToMealPlan$ = this.isMealPlanningEnabled$.pipe(
			switchMap(() =>
				this.recipeId$.pipe(
					withLatestFrom(this.mealPlanningData$),
					map(([recipeId, { selectedMeals }]) => !!selectedMeals.find((meal) => meal.recipe.id === recipeId))
				)
			)
		);
	}

	fetchRecipe(recipeId: number): Observable<RecipeResponse> {
		return this.recipeService.getRecipeDetail(recipeId).pipe(
			catchError((err) => {
				this.errors$.next(true);

				if (isPlatformServer(this.platformId)) {
					this.response?.status(err.status);
				}

				if (err.status === 404) {
					this.router.navigate([NOT_FOUND_PATH], { skipLocationChange: true });
				} else if (err.status >= 500 && err.status < 600) {
					this.router.navigate([ERROR_PATH], { skipLocationChange: true });
				}

				return EMPTY;
			}),
			tap((recipe) => {
				// when loading the recipe check the url against the returned recipe info and redirect to the correct recipe url if the requested url is different. This is for SEO optimisation where, for example, a recipe title has been updated on the same id. It should help remove the old title from any search indexes.
				if (
					this.recipePath(recipe) !== this.router.url &&
					!this.isUTMIncludedInURLQueryParams(this.router.url)
				) {
					// bypass the queryparams with utm for campaign market analysis
					this.response.status(302);
					this.response.set('location', this.recipePath(recipe));
				}
				if (isPlatformBrowser(this.platformId)) {
					this.tealiumService.link({
						tealium_event: 'recipe_view',
						interaction_source: 'page',
						recipe_name: [recipe.title],
						recipe_id: [recipe.id],
						recipe_category: ['Recipes'],
						recipe_prep_time: [recipe.prepDuration],
						recipe_cook_time: [recipe.cookingDuration],
						recipe_servings: [recipe.servings],
						recipe_dietary: [safeStringSplit(recipe.nutritionTags, ', ')],
					});
				}
			})
		);
	}

	isUTMIncludedInURLQueryParams(url: string): boolean {
		const paramArr = url.slice(url.indexOf('?') + 1).split('&');
		return paramArr.some((param) => param.split('=')[0].includes('utm_'));
	}

	handleSave(): void {
		if (!this.recipeId) {
			return;
		}
		this.datalayerService.trackRecipeEvent(NotificationType.Button, 'RDP_save');

		if (!this.shopperService.isShopper) {
			this.modalOverlayService.open({
				eventType: ModalEvent.saveRecipe,
			});
			return;
		}

		this.saveButtonState$.next(ButtonState.busy);
		if (this.recipeSaved$.getValue()) {
			this.recipeService
				.removeSavedRecipe(this.recipeId)
				.pipe(takeUntil(this.destroyed$), isStatusCode(204))
				.subscribe(() => {
					this.recipeSaved$.next(false);
					this.saveButtonState$.next(ButtonState.default);
				});
		} else {
			this.recipe$.pipe(takeUntil(this.destroyed$)).subscribe((recipe) => {
				this.tealiumService.link({
					tealium_event: 'recipe_save',
					interaction_source: 'page',
					recipe_name: [recipe.title],
					recipe_id: [recipe.id],
					recipe_category: ['Recipes'],
					recipe_prep_time: [recipe.prepDuration],
					recipe_cook_time: [recipe.cookingDuration],
					recipe_servings: [recipe.servings],
					recipe_dietary: [safeStringSplit(recipe.nutritionTags, ', ')],
				});
			});

			this.recipeService
				.saveRecipe(this.recipeId)
				.pipe(takeUntil(this.destroyed$), isStatusCode(204))
				.subscribe((res) => {
					this.recipeSaved$.next(res);
					this.saveButtonState$.next(ButtonState.default);
				});
		}
	}

	seeMoreRecipesClick(): void {
		this.datalayerService.trackRecipeEvent(NotificationType.Button, 'RDP_see_more_recipes', this.recipeId);
	}

	recipePath(recipe: RecipeResponse): string {
		const mealtime = mealtimeSlug(recipe.mealtimes?.split(',') ?? []);
		const recipeSlugTitle = slugify(recipe.title);

		if (!mealtime || !recipe.id || !recipeSlugTitle) {
			return `/recipes`;
		}

		return `/recipes/${mealtime}/${recipe.id}/${recipeSlugTitle}`;
	}

	public handleAddToMealPlan(): void {
		this.isLoading$.next(true);
		combineLatest([this.recipe$, this.mealPlanningData$])
			.pipe(
				takeUntil(this.destroyed$),
				switchMap(([recipeResponse, mealPlanningData]: [RecipeResponse, MealPlanningData]) => {
					const hasCompletedPreferences = this.mealPlanningService.hasCompletedPreferences(
						mealPlanningData.preferences
					);
					const recipe = mapRecipeResponseToRecipeStampLocal(recipeResponse);
					const addedServings = mapHouseholdToServings(getHouseholdPreference(mealPlanningData.preferences));
					const shortlist = mealPlanningData.selectedMeals || [];
					const mealPlanStamp = { recipe, mealPlan: { addedServings } };

					if (!hasCompletedPreferences) {
						this.modalRoutingService.openWithOptions(
							MODAL_ROUTES.RECIPE_MODAL,
							{ state: { recipeToAdd: mealPlanStamp } },
							MODAL_ROUTES.MEAL_PLANNING_CHOOSE_PLAN_PATH
						);
						return of(hasCompletedPreferences);
					}

					return this.mealPlanningService
						.persistShortlistedMeal(mealPlanStamp, shortlist)
						.pipe(map(() => hasCompletedPreferences));
				})
			)
			.subscribe((hasCompletedPreferences) => {
				this.isLoading$.next(false);
				/** Navigate to choose meals page has completed meal planning onboarding */
				if (hasCompletedPreferences) {
					return this.router.navigate([MEAL_PLANNING_PATH, MEAL_PLANNING_CHOOSE_MEALS_PATH]);
				}
			});
	}

	public handleViewMealPlan(): void {
		this.router.navigate([MEAL_PLANNING_PATH, MEAL_PLANNING_CHOOSE_MEALS_PATH]);
	}
}
